import React, { useState, useRef, useEffect,useContext } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrashAlt,
  faGripLines,
  faCog,
  faArrowUp,
  faArrowDown,
} from "@fortawesome/free-solid-svg-icons";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { ListGroup } from "react-bootstrap";
import HoverDropdown from "./HoverDropdown";
import RowColumnProperties from "./RowColumnProperties";
import { FormattableInput } from "./FormattableInput";
import "./styles/RowColumnComponent.css";
import VariableContext from "../contexts/VariableContext";
import FetchingFreq from "../contexts/FetchingFreq";
import { JsonWebTokenTypes } from "@azure/msal-browser";
import CommonButton from "../elments/CommonButton";

const RowColumnComponent = ({
  items,
  setItems,
  deleteRow,
  label,
  handlePaste,
  addList,
  addadditionalRow,
  list,
  savePropertyChanges,
  screenOutSelections,
  onScreenOutChange,
  qType,
  selectedVariable,
  setList={setList}
}) => {
   const { selectedTitle } = useContext(VariableContext);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemIndex, setItemIndex] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [focusedRowIndex, setFocusedRowIndex] = useState(null);
const [currentListId, setCurrentListId] = useState(null);
  // Add ref for the contentEditable
  const editableRef = useRef(null);
// Load saved list selection for this question and list type
  useEffect(() => {
    if (selectedTitle) {
      const savedSelections = JSON.parse(localStorage.getItem('listSelections') || '{}');
      const questionSelections = savedSelections[selectedTitle];
      if (questionSelections) {
        const listId = questionSelections[label.toLowerCase()];
        if (listId) {
          setCurrentListId(listId);
        }
      }
    }
  }, [selectedTitle, label]);

   // Auto-detect if current items match any list
  useEffect(() => {
    if (items && items.length > 0 && list.length > 0) {
      const currentContent = JSON.stringify(items);
      const matchingList = list.find(listItem => 
        JSON.stringify(listItem[Object.keys(listItem)[0]]) === currentContent
      );
      
      if (matchingList) {
        setCurrentListId(matchingList.id);
        saveListSelection(matchingList.id);
      } else if (currentListId) {
        // Current items don't match the selected list - clear selection
        setCurrentListId(null);
        clearListSelection();
      }
    }
  }, [items, list]);
  // Sync the contentEditable with pasteText state
  // Save list selection to localStorage
  const saveListSelection = (listId) => {
    if (!selectedTitle) return;
    
    const savedSelections = JSON.parse(localStorage.getItem('listSelections') || '{}');
    if (!savedSelections[selectedTitle]) {
      savedSelections[selectedTitle] = {};
    }
    savedSelections[selectedTitle][label.toLowerCase()] = listId;
    localStorage.setItem('listSelections', JSON.stringify(savedSelections));
  };
  // Clear list selection from localStorage
  const clearListSelection = () => {
    if (!selectedTitle) return;
    
    const savedSelections = JSON.parse(localStorage.getItem('listSelections') || '{}');
    if (savedSelections[selectedTitle]) {
      delete savedSelections[selectedTitle][label.toLowerCase()];
      localStorage.setItem('listSelections', JSON.stringify(savedSelections));
    }
  };

  useEffect(() => {
    if (editableRef.current && editableRef.current.innerHTML !== pasteText) {
      // Save cursor position
      const selection = window.getSelection();
      const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

      // Only update if the content is actually different
      if (editableRef.current.innerHTML !== pasteText) {
        editableRef.current.innerHTML = pasteText;

        // Try to restore cursor position
        if (range && editableRef.current.contains(range.startContainer)) {
          try {
            selection.removeAllRanges();
            selection.addRange(range);
          } catch (e) {
            // If cursor restoration fails, place cursor at end
            const newRange = document.createRange();
            newRange.selectNodeContents(editableRef.current);
            newRange.collapse(false);
            selection.removeAllRanges();
            selection.addRange(newRange);
          }
        }
      }
    }
  }, [pasteText]);
  const enhancedDeleteRow = (index) => {
    if (currentListId) {
      setCurrentListId(null);
      clearListSelection();
    }
    deleteRow(index);
  };
const handleListSelection = (selectedKey) => {
  const selectedConfig = list.find(
    (item) => Object.keys(item)[0] === selectedKey
  );

  if (!selectedConfig) return;

  const listItems = selectedConfig[selectedKey];
  const previousListId = currentListId;

  const questionNumber =
    selectedVariable?.Question_number || selectedTitle;

  const typeMap = {
    Rows: "rows",
    Columns: "columns",
    Statements: "statements",
  };
  const currentType = typeMap[label];

  /* ---------------------------------------------
     STEP 1: Decide replace / prepend / append
  --------------------------------------------- */

  let applyMode = "replace";

  if (items && items.length > 0) {
    const choice = window.prompt(
      `How would you like to apply this list?\n\n` +
        `1 → Replace existing ${label}\n` +
        `2 → Append at beginning\n` +
        `3 → Append at end\n\n` +
        `Enter 1, 2 or 3`,
      "1"
    );

    if (!["1", "2", "3"].includes(choice)) {
      alert("❌ Invalid choice. Action cancelled.");
      return;
    }

    applyMode =
      choice === "1"
        ? "replace"
        : choice === "2"
        ? "prepend"
        : "append";
  } else {
    const confirmEmpty = window.confirm(
      `There are no existing ${label.toLowerCase()}.\n\n` +
        `Are you sure you want to add this list here?`
    );
    if (!confirmEmpty) return;
  }

  /* ---------------------------------------------
     STEP 2: Remove from previous list if needed
  --------------------------------------------- */

  if (previousListId && previousListId !== selectedConfig.id) {
    setList((prevList) =>
      prevList
        .map((item) => {
          if (item.id === previousListId) {
            const qNums = item.questionNumbers || [];
            const updatedQNums = qNums.filter(
              (q) =>
                !(
                  q.name === questionNumber &&
                  q.type === currentType
                )
            );

            if (updatedQNums.length === 0) return null;

            const names = [...new Set(updatedQNums.map((q) => q.name))].sort();
            const types = [...new Set(updatedQNums.map((q) => q.type))];

            const typeIdentifier =
              types.length > 1
                ? `[Mixed:${types.join("/")}]`
                : `[${types[0]}]`;

            const newKey =
              names.length > 1
                ? `${names.join("_")}${typeIdentifier}`
                : `${names[0]}${typeIdentifier}`;

            return {
              [newKey]: item[Object.keys(item)[0]],
              type: item.type,
              questionNumbers: updatedQNums,
              usageCount: updatedQNums.length,
              id: item.id,
            };
          }
          return item;
        })
        .filter(Boolean)
    );
  }

  /* ---------------------------------------------
     STEP 3: Apply list to current items
  --------------------------------------------- */

  let updatedItems = [];

  if (applyMode === "replace") {
    updatedItems = listItems;
  } else if (applyMode === "prepend") {
    updatedItems = [...listItems, ...(items || [])];
  } else {
    updatedItems = [...(items || []), ...listItems];
  }

  savePropertyChanges(updatedItems, label);

  /* ---------------------------------------------
     STEP 4: Track list usage (UNCHANGED)
  --------------------------------------------- */

  setCurrentListId(selectedConfig.id);
  saveListSelection(selectedConfig.id);

  const currentQuestionNumbers =
    selectedConfig.questionNumbers || [];

  const alreadyLinked = currentQuestionNumbers.find(
    (q) => q.name === questionNumber && q.type === currentType
  );

  let displayKey = selectedKey;

  if (!alreadyLinked) {
    const updatedQuestionNumbers = [
      ...currentQuestionNumbers,
      { name: questionNumber, type: currentType },
    ];

    const names = [...new Set(updatedQuestionNumbers.map((q) => q.name))].sort();
    const types = [...new Set(updatedQuestionNumbers.map((q) => q.type))];

    const typeIdentifier =
      types.length > 1
        ? `[Mixed:${types.join("/")}]`
        : `[${types[0]}]`;

    const newKey =
      names.length > 1
        ? `${names.join("_")}${typeIdentifier}`
        : `${names[0]}${typeIdentifier}`;

    setList((prevList) =>
      prevList.map((item) => {
        if (Object.keys(item)[0] === selectedKey) {
          return {
            [newKey]: listItems,
            type: selectedConfig.type,
            questionNumbers: updatedQuestionNumbers,
            usageCount: updatedQuestionNumbers.length,
            id: selectedConfig.id,
          };
        }
        return item;
      })
    );

    displayKey = newKey;
  }

  /* ---------------------------------------------
     STEP 5: Final feedback
  --------------------------------------------- */

  const modeText =
    applyMode === "replace"
      ? "Replaced"
      : applyMode === "prepend"
      ? "Appended at beginning"
      : "Appended at end";

  alert(
    `✅ List "${displayKey}" applied successfully\n\n` +
      `Action: ${modeText}\n` +
      `Applied to: ${questionNumber} (${currentType})`
  );
};



  // Enhanced list data for HoverDropdown with usage information
  const getEnhancedListData = () => {
    return list
      .map(item => {
        const key = Object.keys(item)[0];
        const isSelected = item.id === currentListId;
        
        return {
          value: key,
          label: (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
              padding: '4px 8px',
              borderRadius: '4px'
            }}>
              <span style={{ fontWeight: isSelected ? 'bold' : 'normal' }}>
                {key}
              </span>
              <div style={{ display: 'flex', gap: '8px', fontSize: '11px', color: '#666' }}>
                <span>{item.usageCount || 1} question{item.usageCount > 1 ? 's' : ''}</span>
                <span>{item[key].length} items</span>
                {isSelected && <span>✅</span>}
              </div>
            </div>
          )
        };
      });
  };
  const openPropertiesModal = (item, index) => {
    setSelectedItem(item);
    setItemIndex(index);
    setIsModalOpen(true);
  };

  const handlePropertyChange = (index, property, value) => {
    setSelectedItem({ ...selectedItem, [property]: value });
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], [property]: value };
    savePropertyChanges(updatedItems, label);
  };

 // Enhanced handleItemChange to detect when items no longer match selected list
  const handleItemChange = (index, value) => {
    const updatedItems = [...items];
    updatedItems[index].text = value;
    
    // Check if items still match the selected list
    if (currentListId) {
      const selectedList = list.find(item => item.id === currentListId);
      if (selectedList) {
        const selectedListItems = selectedList[Object.keys(selectedList)[0]];
        if (JSON.stringify(updatedItems) !== JSON.stringify(selectedListItems)) {
          // Items no longer match - clear selection
          setCurrentListId(null);
          clearListSelection();
        }
      }
    }
    
    savePropertyChanges(updatedItems, label);
  };

// Enhanced addItem to clear list selection when manually adding items
  const addItem = () => {
    const newItem = {
      id: `${label === "Rows" ? "r" : "c"}${items ? items.length + 1 : 1}`,
      text: "",
    };
    
    const updatedItems = items ? [...items, newItem] : [newItem];
    
    // Clear list selection when manually adding items
    if (currentListId) {
      setCurrentListId(null);
      clearListSelection();
    }
    
    savePropertyChanges(updatedItems, label);
  };  

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const updatedItems = Array.from(items);
    const [movedItem] = updatedItems.splice(result.source.index, 1);
    updatedItems.splice(result.destination.index, 0, movedItem);

    if (typeof setItems === "function") {
      setItems(updatedItems);
    } else if (typeof savePropertyChanges === "function") {
      savePropertyChanges(updatedItems, label);
    } else {
      console.error("Neither setItems nor savePropertyChanges is available");
    }
  };

  const sortByIndexAscending = () => {
    items.forEach((row, lineIndex) => {
      row.index = lineIndex + 1;
    });
  };

  const sortByIndexDescending = () => {
    var lenrow = items.length;
    items.forEach((row, lineIndex) => {
      row.index = lenrow;
      lenrow = lenrow - 1;
    });
  };

  const cleanPastedHTML = (dirty) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(dirty, "text/html");

    // Allowed inline and block formatting tags — no table tags!
    const allowedTags = [
      "B",
      "I",
      "U",
      "STRONG",
      "EM",
      "BR",
      "P",
      "DIV",
      "OL",
      "UL",
      "LI",
      "SPAN",
    ];

    const allowedStyles = [
      "color",
      "background-color",
      "font-weight",
      "font-style",
      "text-decoration",
    ];

    const cleanNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        // Clean text content - replace multiple spaces with single space
        // and handle Word's non-breaking spaces
        let text = node.textContent
          .replace(/\u00A0/g, " ") // Replace non-breaking spaces with regular spaces
          .replace(/\s+/g, " ") // Replace multiple spaces with single space
          .trim(); // Trim whitespace

        // Only return text if it's not empty after cleaning
        return text || "";
      }

      // ❌ If it's a table-related tag, flatten it (remove structure, keep content)
      if (["TABLE", "TBODY", "TR", "TD", "TH"].includes(node.nodeName)) {
        const content = Array.from(node.childNodes)
          .map(cleanNode)
          .filter((text) => text.trim()) // Remove empty strings
          .join(" ")
          .replace(/\s{2,}/g, " ")
          .trim();
        return content;
      }

      // Handle ordered lists - convert to individual items
      if (node.nodeName === "OL") {
        const listItems = Array.from(node.querySelectorAll("li"))
          .map((li) => {
            const content = Array.from(li.childNodes)
              .map(cleanNode)
              .join("")
              .trim();
            return content ? `<div>${content}</div>` : "";
          })
          .filter((item) => item)
          .join("");
        return listItems;
      }

      // Handle unordered lists - convert to individual items
      if (node.nodeName === "UL") {
        const listItems = Array.from(node.querySelectorAll("li"))
          .map((li) => {
            const content = Array.from(li.childNodes)
              .map(cleanNode)
              .join("")
              .trim();
            return content ? `<div>${content}</div>` : "";
          })
          .filter((item) => item)
          .join("");
        return listItems;
      }

      // Handle list items - clean them individually
      if (node.nodeName === "LI") {
        const content = Array.from(node.childNodes)
          .map(cleanNode)
          .join("")
          .trim();
        return content ? `<div>${content}</div>` : "";
      }

      // Remove Word-specific classes and empty spans
      if (
        node.nodeName === "SPAN" &&
        (!node.innerHTML.trim() || node.classList.contains("MsoNormal"))
      ) {
        return Array.from(node.childNodes).map(cleanNode).join("");
      }

      // Remove Word-specific comments and processing instructions
      if (
        node.nodeType === Node.COMMENT_NODE ||
        node.nodeType === Node.PROCESSING_INSTRUCTION_NODE
      ) {
        return "";
      }

      // Remove Word's style definitions and scripts
      if (
        node.nodeName === "STYLE" ||
        node.nodeName === "SCRIPT" ||
        node.nodeName === "META"
      ) {
        return "";
      }

      // If tag not allowed — unwrap it but keep children
      if (!allowedTags.includes(node.nodeName)) {
        return Array.from(node.childNodes).map(cleanNode).join("");
      }

      // Clean up Word's class attributes
      if (node.hasAttribute("class")) {
        const classes = node.getAttribute("class").split(" ");
        const wordClasses = ["MsoNormal", "MsoListParagraph", "MsoBodyText"];
        const filteredClasses = classes.filter(
          (cls) => !wordClasses.includes(cls)
        );
        if (filteredClasses.length > 0) {
          node.setAttribute("class", filteredClasses.join(" "));
        } else {
          node.removeAttribute("class");
        }
      }

      // Remove Word-specific attributes
      const wordAttributes = ["lang", "style", "type", "start"];
      wordAttributes.forEach((attr) => {
        if (node.hasAttribute(attr)) {
          node.removeAttribute(attr);
        }
      });

      // Keep only allowed inline styles
      if (node.style && node.style.length > 0) {
        const styles = [];
        allowedStyles.forEach((styleProp) => {
          const value = node.style.getPropertyValue(styleProp);
          if (value) styles.push(`${styleProp}: ${value}`);
        });
        if (styles.length > 0) {
          node.setAttribute("style", styles.join("; "));
        } else {
          node.removeAttribute("style");
        }
      }

      // Recursively clean children
      const childContent = Array.from(node.childNodes)
        .map(cleanNode)
        .filter((text) => text.trim()) // Remove empty text nodes
        .join("");

      // If no content after cleaning, return empty string
      if (!childContent.trim()) {
        return "";
      }

      // For paragraphs and divs, only wrap if they have content
      if (["P", "DIV"].includes(node.nodeName) && childContent.trim()) {
        return `<${node.nodeName.toLowerCase()}>${childContent}</${node.nodeName.toLowerCase()}>`;
      }

      // For inline elements, only wrap if they have content
      if (
        ["B", "I", "U", "STRONG", "EM", "SPAN"].includes(node.nodeName) &&
        childContent.trim()
      ) {
        // Only keep span if it has meaningful styles
        if (node.nodeName === "SPAN" && !node.getAttribute("style")) {
          return childContent;
        }
        return `<${node.nodeName.toLowerCase()}>${childContent}</${node.nodeName.toLowerCase()}>`;
      }

      return childContent;
    };

    let cleanedContent = Array.from(doc.body.childNodes)
      .map(cleanNode)
      .filter((text) => text.trim()) // Remove empty nodes
      .join("");

    // Additional processing for numbered lists in plain text format
    if (!cleanedContent.includes("<div>") && !cleanedContent.includes("<li>")) {
      // Split by common list patterns
      const lines = cleanedContent.split(/\n/);
      const processedLines = lines
        .map((line) => {
          // Remove numbered list markers (1., 2., etc.)
          line = line.replace(/^\s*\d+\.\s*/, "").trim();
          // Remove bullet points
          line = line.replace(/^[•\-]\s*/, "").trim();
          return line;
        })
        .filter((line) => line.trim());

      if (processedLines.length > 1) {
        cleanedContent = processedLines
          .map((line) => `<div>${line}</div>`)
          .join("");
      }
    }

    // Final cleanup
    cleanedContent = cleanedContent
      .replace(/\n\s*\n/g, "\n") // Replace multiple newlines with single newline
      .replace(/(<br\s*\/?>\s*){2,}/gi, "<br><br>") // Limit consecutive <br> tags
      .trim();

    return cleanedContent || "";
  };

  // Handle paste from the text box
  const handleTextBoxPaste = (mode) => {
    if (!pasteText.trim() && !pasteText.replace(/<[^>]*>/g, "").trim()) {
      alert("Please paste some text first");
      return;
    }

    if (
      (mode === "insertBefore" || mode === "insertAfter") &&
      focusedRowIndex === null
    ) {
      alert("Please click on a row first to set the insertion point");
      return;
    }

    // 🧹 Clean the pasted HTML before using it
    const cleanedHTML = cleanPastedHTML(pasteText);

    // Create a synthetic paste event with sanitized HTML
    const syntheticEvent = {
      preventDefault: () => {},
      clipboardData: {
        getData: (type) => {
          if (type === "text/html") {
            return cleanedHTML || "";
          }
          // Extract plain text from cleaned HTML if needed
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = cleanedHTML;
          return tempDiv.textContent || tempDiv.innerText || "";
        },
      },
      pasteMode: mode,
      skipModal: true,
      insertIndex: focusedRowIndex,
    };

    // ✅ Call the main paste handler
    handlePaste(syntheticEvent, label, mode);

    // Clear text box
    setPasteText("");
  };

  return (
    <div style={{ display: "flex", gap: "20px", width: "100%" }}>

      {/* Left side - existing rows/columns component */}
      <div
        className={`${label.toLowerCase()}-section`}
        style={{ flex: "1", minWidth: "400px" }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <h4 style={{ margin: "0" }}>{label}</h4>
          {items && (
            <div style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}>
<CommonButton symbol={faArrowUp} title="Sort Ascending (1 to N)" onClick={sortByIndexAscending} variant="primary"  size="small"/>
<CommonButton symbol={faArrowDown} title="Sort Descending (N to 1)" onClick={sortByIndexDescending} variant="primary" size="small"/>
<CommonButton label="Save List" title="Save to List" onClick={() => addList(label)} variant="success" size="small"/>            
  
</div>
          )}
        {list && list.length>0 &&  (<div style={{ marginBottom: '15px' }}>
            <HoverDropdown
              text={currentListId ? "✓ List Selected" : "Select List"}
              exportfeatures={getEnhancedListData()}
              feature={6}
              handleSelection={handleListSelection}
            />
           
          {/* Show current list status */}
          {currentListId && (
            <div style={{ 
              fontSize: '12px', 
              color: '#28a745',
              marginTop: '8px',
              padding: '8px',
              backgroundColor: '#d4edda',
              borderRadius: '4px',
              border: '1px solid #c3e6cb'
            }}>
              ✅ Linked to shared list. Changes here will update all questions using this list {currentListId}.
            </div>
          )}
        </div>)}
        </div>
        {/* List Selection with Enhanced Display */}
        

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId={label.toLowerCase()}>
            {(provided, snapshot) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                onPaste={(e) => handlePaste(e, label)}
                style={{
                  backgroundColor: snapshot.isDraggingOver
                    ? "#f8f9fa"
                    : "transparent",
                  borderRadius: "4px",
                  padding: "4px",
                  minHeight: "100px",
                }}
              >
                {items &&
                  items.map((item, index) => {
                    const isTerminated =
                      screenOutSelections?.[index] || item.screenout || false;

                    return (
                      <Draggable
                        key={index}
                        draggableId={`${label.toLowerCase()}-${index}`}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`row-item ${
                              snapshot.isDragging ? "dragging" : ""
                            }`}
                            style={{
                              ...provided.draggableProps.style,
                              backgroundColor:
                                focusedRowIndex === index
                                  ? "#f1f1f1"
                                  : snapshot.isDragging
                                  ? "#f1f1f1"
                                  : "#f9f9f9",
                            }}
                            onClick={() => setFocusedRowIndex(index)}
                          >
                            <div
                              className="row-grip"
                              {...provided.dragHandleProps}
                              onClick={(e) => e.stopPropagation()}
                            >
             {item.index }
                              <FontAwesomeIcon icon={faGripLines} />
                            </div>
             
                            <FormattableInput
                              value={item.text}
                              onChange={(e) => {
                                handleItemChange(index, e.target.value);
                              }}
                              onClick={() => setFocusedRowIndex(index)}
                              onFocus={() => setFocusedRowIndex(index)}
                              type="contentEditable"
                              style={{
                                flex: "1",
                                minWidth: "0",
                                border: "1px solid #ccc",
                                padding: "8px",
                                borderRadius: "4px",
                                outline: "none",
                                backgroundColor: "#fff",
                                fontSize: "14px",
                                marginRight: "12px",
                                transition: "border-color 0.3s ease",
                              }}
                            />

                <CommonButton label="" symbol={faTrashAlt}  title="Delete Question" onClick={(e) => {
                                e.stopPropagation();
                                deleteRow(index);
                              }} variant="danger" />

                            <label
                              className="terminate-checkbox"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={isTerminated}
                                onChange={(e) =>
                                  onScreenOutChange(index, e.target.checked)
                                }
                              />
                              T
                            </label>
                    <CommonButton label="" title="Settings" symbol={faCog} onClick={(e) => {
                                e.stopPropagation();
                                openPropertiesModal(item, index);
                              }} />
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <CommonButton label={`+ New ${label}`} title={`Add new ${label}`} onClick={() => addadditionalRow(label)} variant="tab"/>

        <RowColumnProperties
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          item={selectedItem}
          index={itemIndex}
          handlePropertyChange={handlePropertyChange}
          qType={qType}
        />
      </div>

      {/* Right side - Paste text box */}
      <div className="paste-section">
        <h4 style={{ margin: "0" }}>Paste {label}</h4>

        <div
          ref={editableRef}
          contentEditable
          onInput={(e) => setPasteText(e.currentTarget.innerHTML)}
          onPaste={(e) => {
            e.preventDefault();

            // Get both HTML and plain text versions
            const htmlContent = e.clipboardData.getData("text/html");
            const plainContent = e.clipboardData.getData("text/plain");

            let cleaned;
            if (htmlContent) {
              // Use HTML content but clean it thoroughly
              cleaned = cleanPastedHTML(htmlContent);
            } else {
              // Fallback to plain text with proper line breaks
              cleaned = plainContent
                .split("\n")
                .map((line) => line.trim())
                .filter((line) => line)
                .map((line) => `<div>${line}</div>`)
                .join("");
            }

            // Insert the cleaned content
            const selection = window.getSelection();
            if (selection.rangeCount > 0) {
              const range = selection.getRangeAt(0);
              range.deleteContents();

              const tempDiv = document.createElement("div");
              tempDiv.innerHTML = cleaned;

              const frag = document.createDocumentFragment();
              while (tempDiv.firstChild) {
                frag.appendChild(tempDiv.firstChild);
              }

              range.insertNode(frag);
              range.collapse(false);

              // Update state
              setPasteText(editableRef.current.innerHTML);
            }
          }}
          className="paste-textbox"
          data-placeholder={`Paste your ${label.toLowerCase()} here...`}
        />

        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <div style={{ display: "flex", gap: "8px" }}>
        <CommonButton label="Prepend" title="Add items before all existing items" onClick={() => handleTextBoxPaste("prepend")} variant="tab"/>    
        <CommonButton label="Append" title="Add items after all existing items" onClick={() => handleTextBoxPaste("append")} variant="tab"/>

        <CommonButton label="Insert Before" title="Insert items before selected row" onClick={() => handleTextBoxPaste("insertBefore")} variant="tab"/>    
        <CommonButton label="Insert After" title="Insert items after selected row" onClick={() => handleTextBoxPaste("insertAfter")} variant="tab"/>
          </div>
        
        
        <CommonButton label="Replace" title="Replace all existing items" onClick={() => handleTextBoxPaste("replace")} variant="tab"/>
        </div>
        <CommonButton label="Clear" title="Clear the clipboard" onClick={() => setPasteText("")} variant="tab"/>
      </div>

    </div>
  );
};

export default RowColumnComponent;
